import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  Paintbrush, 
  Zap, 
  Droplets, 
  Home, 
  Lightbulb, 
  Wrench,
  ShieldCheck,
  Sparkles,
  CheckCircle2,
  Box,
  Layers,
  LayoutGrid
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const services = [
  {
    icon: Paintbrush,
    title: 'Painting Services',
    subtitle: 'Modern Painting Solutions',
    description: 'Bring color and life to your walls with our expert painting services.',
    features: [
      'Interior & Exterior Painting',
      'Designer Walls & Textures',
      'Waterproof Coating',
      'Repainting & Maintenance',
    ],
    color: 'from-orange-400/20 to-amber-500/10',
    iconBg: 'bg-orange-500/10',
    iconColor: 'text-orange-600',
  },
  {
    icon: Zap,
    title: 'Electrical Services',
    subtitle: 'Expert Electrical Solutions',
    description: 'Brighten your home with safe and modern electrical installations.',
    features: [
      'House Wiring & Fittings',
      'Switchboards & Panels',
      'Lighting & Smart Controls',
      'Maintenance & Safety Checks',
    ],
    color: 'from-yellow-400/20 to-amber-500/10',
    iconBg: 'bg-yellow-500/10',
    iconColor: 'text-yellow-600',
  },
  {
    icon: Droplets,
    title: 'Plumbing Services',
    subtitle: 'Reliable Plumbing Solutions',
    description: 'Smart plumbing solutions for modern homes with expert care.',
    features: [
      'Bathroom & Kitchen Fittings',
      'Leak Repair & Maintenance',
      'Water Tank & Pipeline Installation',
      'Modern Sanitary Upgrades',
    ],
    color: 'from-blue-400/20 to-cyan-500/10',
    iconBg: 'bg-blue-500/10',
    iconColor: 'text-blue-600',
  },
];

const additionalServices = [
  { icon: Home, title: 'Complete Construction' },
  { icon: Lightbulb, title: 'Smart Home Solutions' },
  { icon: Wrench, title: 'Renovation Works' },
  { icon: ShieldCheck, title: 'Safety Inspections' },
  { icon: Sparkles, title: 'Interior Design' },
  { icon: Box, title: 'Aluminium Work' },
  { icon: Layers, title: 'CPVC Work' },
  { icon: LayoutGrid, title: 'Flooring — Tiles, Granite' },
];

export const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-20 lg:py-28 bg-muted/30" ref={ref}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-primary font-medium uppercase tracking-wider text-sm">
            Our Services
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mt-4 mb-6">
            Painting • Electrical • Plumbing —{' '}
            <span className="text-gradient-gold">All Under One Roof</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Experience hassle-free home transformation with our comprehensive services. 
            One call, complete solutions.
          </p>
        </motion.div>

        {/* Main Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
            >
              <Card className="h-full card-premium border-border/50 overflow-hidden group">
                {/* Gradient Top Bar */}
                <div className={`h-1.5 bg-gradient-to-r ${service.color}`} />
                
                <CardHeader className="pb-4">
                  <div className="flex items-start gap-4">
                    <motion.div
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                      className={`service-icon ${service.iconBg}`}
                    >
                      <service.icon className={`w-7 h-7 ${service.iconColor}`} />
                    </motion.div>
                    <div>
                      <CardTitle className="font-display text-xl text-foreground mb-1">
                        {service.title}
                      </CardTitle>
                      <p className="text-sm text-primary font-medium">
                        {service.subtitle}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="flex flex-col h-[calc(100%-120px)]">
                  <p className="text-muted-foreground mb-6">
                    {service.description}
                  </p>
                  
                  <ul className="space-y-3 mb-6 flex-grow">
                    {service.features.map((feature, i) => (
                      <motion.li
                        key={feature}
                        initial={{ opacity: 0, x: -20 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.4, delay: 0.4 + i * 0.1 }}
                        className="flex items-center gap-3 text-sm text-foreground"
                      >
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                        {feature}
                      </motion.li>
                    ))}
                  </ul>
                  
                  <Button
                    onClick={scrollToContact}
                    variant="outline"
                    className="w-full border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground transition-all mt-auto"
                  >
                    Get Quote
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Additional Services */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="bg-card rounded-2xl p-8 shadow-card"
        >
          <h3 className="font-display text-xl font-semibold text-foreground mb-6 text-center">
            Additional Services We Offer
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            {additionalServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                whileHover={{ scale: 1.05, y: -2 }}
                className="flex items-center gap-2 bg-muted px-4 py-2 rounded-full"
              >
                <service.icon className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-foreground">{service.title}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-2 bg-success/10 text-success px-4 py-2 rounded-full">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-success" />
            </span>
            <span className="text-sm font-medium">Book Your Free Home Visit Today!</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
